import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { EventService } from '../../services/event.service';
import { AttendeeService } from '../../services/attendee.service';
import { CallPlanService } from '../../services/call-plan.service';
import { Event } from '../../models/event.model';
import { RegistrationFormData, CallPlan } from '../../models/attendee.model';
import { EvaluationQuestion } from '../../models/question.model';
import { environment } from '../../../environments/environment';
import { finalize, takeUntil, catchError, map } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { US_STATES, DEGREE_OPTIONS, HCP_OPTIONS } from '../../constants/states.constants';

@Component({
  selector: 'app-event-registration',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './event-registration.html',
  styleUrl: './event-registration.css',
})
export class EventRegistrationComponent implements OnInit, OnDestroy {
  event: Event | null = null;
  step = 1;
  loading = false;
  checkingCallPlan = false;
  error: string | null = null;
  missingFields: string[] = [];
  isNavigating = false;
  isCallPlanVerified = false;
  private eventLoadAttempted = false;
  
  registrationForm: FormGroup;
  confirmDialogVisible = false;
  callPlanData: CallPlan | null = null;

  readonly hcpOptions = HCP_OPTIONS;
  degreeOptions: string[] = [];
  stateOptions: string[] = [];
  specialtyOptions: string[] = [];
  contactTypeOptions: string[] = [];
  
  registrationQuestions: EvaluationQuestion[] = [];
  loadingQuestions = false;
  useDynamicQuestions = false;
  
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private eventService: EventService,
    private attendeeService: AttendeeService,
    private callPlanService: CallPlanService,
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {
    this.registrationForm = this.createForm();
  }

  ngOnInit() {
    if (this.eventLoadAttempted) {
      return;
    }
    
    if (this.isNavigating) {
      return;
    }
    
    const params = this.route.snapshot.queryParams;
    const token = params['token'];
    
    this.eventLoadAttempted = true;
    
    if (token) {
      this.loading = true;
      this.error = null;
      this.eventService.getEventByToken(token).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      ).subscribe({
        next: (event) => {
          console.log('Event received:', event);
          
          if (event) {
            this.event = event;
            this.error = null;
            this.loading = false; // Set loading to false when event is received
            console.log('Event set, loading:', this.loading, 'event:', this.event);
            
            // Fetch dropdowns directly from API
            if (event.companyId) {
              // Fetch credentials and specialty from company dropdown endpoint
              this.eventService.getCompanyDropdown(event.companyId, 'attendee_degree').pipe(
                takeUntil(this.destroy$),
                catchError(err => {
                  console.error('Error fetching credential options:', err);
                  this.degreeOptions = [...DEGREE_OPTIONS];
                  return of([]);
                })
              ).subscribe(options => {
                if (options && options.length > 0) {
                  this.degreeOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
                } else {
                  this.degreeOptions = [...DEGREE_OPTIONS];
                }
                this.cdr.detectChanges();
              });
              
              this.eventService.getCompanyDropdown(event.companyId, 'attendee_specialty').pipe(
                takeUntil(this.destroy$),
                catchError(err => {
                  console.error('Error fetching specialty options:', err);
                  this.specialtyOptions = [];
                  return of([]);
                })
              ).subscribe(options => {
                if (options && options.length > 0) {
                  this.specialtyOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
                }
                this.cdr.detectChanges();
              });
            }
            
            // Fetch states from common dropdown endpoint
            // Note: States might not be in dropdown format, but we'll try the endpoint as requested
            // If it returns empty, we'll fall back to the /states endpoint or constants
            this.eventService.getCommonDropdown('country_state').pipe(
              takeUntil(this.destroy$),
              catchError(err => {
                console.error('Error fetching state options from dropdown:', err);
                // Fallback: try to get states from /states endpoint if dropdown doesn't work
                return this.http.get<Array<{ name: string; code: string }>>(`${environment.apiUrl}/api/states?country_id=US`).pipe(
                  map(states => states.map(s => ({ value: s.name, label: s.name }))),
                  catchError(() => {
                    console.warn('Both dropdown and /states endpoint failed, using constants');
                    this.stateOptions = [...US_STATES];
                    return of([]);
                  })
                );
              })
            ).subscribe(options => {
              if (options && options.length > 0) {
                this.stateOptions = options.map(opt => opt.value || opt.label || '').filter(opt => opt !== '');
              } else {
                // Fallback to constants if dropdown doesn't return data
                this.stateOptions = [...US_STATES];
              }
              this.cdr.detectChanges();
            });
            
            // Fetch registration questions
            this.loadRegistrationQuestions(event.id, token);
            
            // Force change detection to ensure template updates
            this.cdr.detectChanges();
          } else {
            this.error = 'Event data is empty. Please try again.';
            this.event = null;
            this.loading = false;
          }
        },
        error: (err) => {
          this.error = 'An error occurred while loading the event. Please try again.';
          this.event = null;
          this.loading = false;
          console.error('Error loading event:', err);
        }
      });
    } else {
      this.error = 'No event specified. A registration token is required.';
    }
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  createForm(): FormGroup {
    return this.fb.group({
      step: [1],
      first_name: ['', Validators.required],
      last_name: ['', Validators.required],
      is_hcp: ['', Validators.required],
      cats_num: [''],
      license_state: [''],
      license: [''],
      degrees: ['', Validators.required],
      degrees_other: [''],
      subspecialty: ['', Validators.required],
      subspecialty_other: [''],
      contact_type: ['', Validators.required],
      contact_type_other: [''],
      office: this.fb.group({
        affiliation: ['', Validators.required],
        address1: ['', Validators.required],
        address2: [''],
        city: ['', Validators.required],
        state: ['', Validators.required],
        zip: ['', Validators.required],
        phone: [''],
        email: ['', [Validators.required, Validators.email]]
      }),
      confirm_email: ['', [Validators.required, Validators.email]],
      auto_populate: ['0'],
      compass_id: [''],
      call_plan_id: [null]
    });
  }

  loadEvent(eventId: number | string) {
    this.loading = true;
    this.error = null;
    
    this.eventService.getEvent(eventId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (event) => {
        this.event = event;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = 'An error occurred while loading the event. Please try again.';
        console.error('Error loading event:', err);
      }
    });
  }

  get isHcpLicensed(): boolean {
    return this.registrationForm.get('is_hcp')?.value === 'HCP_LICENSED';
  }

  get isHcpNotLicensed(): boolean {
    return this.registrationForm.get('is_hcp')?.value === 'HCP_NOT_LICENSED';
  }

  get showHcpFields(): boolean {
    return this.isHcpLicensed || this.isHcpNotLicensed;
  }

  get degreesValue(): string {
    return this.registrationForm.get('degrees')?.value || '';
  }

  get showDegreesOther(): boolean {
    return this.degreesValue === 'Other';
  }

  get subspecialtyValue(): string {
    return this.registrationForm.get('subspecialty')?.value || '';
  }

  get showSubspecialtyOther(): boolean {
    return this.subspecialtyValue === 'Other';
  }

  get contactTypeValue(): string {
    return this.registrationForm.get('contact_type')?.value || '';
  }

  get showContactTypeOther(): boolean {
    return this.contactTypeValue === 'Other';
  }

  onStep1Submit(event?: SubmitEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('onStep1Submit called, useDynamicQuestions:', this.useDynamicQuestions, 'step:', this.step);
    
    // When using dynamic questions, validate required dynamic question fields
    if (this.useDynamicQuestions) {
      const invalidDynamicFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            // Check if the field is empty or invalid
            const isEmpty = value === null || value === '' || value === undefined || 
                           (Array.isArray(value) && value.length === 0);
            if (control.invalid || isEmpty) {
              invalidDynamicFields.push(controlName);
              control.markAsTouched();
              console.log('Invalid field:', controlName, 'value:', value, 'invalid:', control.invalid);
            }
          } else {
            console.warn('Control not found for question:', controlName);
          }
        }
      });
      
      if (invalidDynamicFields.length > 0) {
        console.log('Validation failed, invalid fields:', invalidDynamicFields);
        this.markFieldsAsMissing(invalidDynamicFields);
        this.error = 'Please complete all required fields.';
        this.cdr.detectChanges();
        return;
      }

      // Validate confirm fields
      for (const eq of this.registrationQuestions) {
        const question = eq.question;
        if (!question || !question.confirm || !this.isQuestionVisible(eq)) continue;
        if (this.isConfirmRequired(eq)) {
          const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
          if (confirmControl && !confirmControl.value?.toString().trim()) {
            invalidDynamicFields.push(`confirm_question_${question.id}`);
            confirmControl.markAsTouched();
          }
        }
        const confirmError = this.getConfirmError(eq);
        if (confirmError) {
          this.error = confirmError;
          this.markFieldsAsMissing([`confirm_question_${question.id}`]);
          this.cdr.detectChanges();
          return;
        }
      }
      if (invalidDynamicFields.length > 0) {
        this.markFieldsAsMissing(invalidDynamicFields);
        this.error = 'Please complete all required fields.';
        this.cdr.detectChanges();
        return;
      }

      console.log('Validation passed');
      // For dynamic questions, all fields are shown at once, so submit directly
      // Don't move to step 2 - just submit the form
      this.submitRegistration();
    } else {
      // Original validation for hardcoded form
      const requiredFields = ['first_name', 'last_name', 'is_hcp'];
      const invalidFields = requiredFields.filter(field => 
        this.registrationForm.get(field)?.invalid
      );
      
      if (invalidFields.length > 0) {
        this.markFieldsAsMissing(invalidFields);
        return;
      }

      // HCP validation (only for hardcoded form)
      const isHcp = this.registrationForm.get('is_hcp')?.value;
      if (isHcp === 'HCP_LICENSED' || isHcp === 'HCP_NOT_LICENSED') {
        if (!this.validateHcpFields(isHcp)) {
          return;
        }
        this.checkCallPlan(this.registrationForm.value);
      } else {
        this.moveToStep2();
      }
    }
  }

  private validateHcpFields(isHcp: string): boolean {
    const catsNum = this.registrationForm.get('cats_num')?.value;
    const license = this.registrationForm.get('license')?.value;

    if (isHcp === 'HCP_LICENSED' && !catsNum && !license) {
      this.error = 'Must enter either NPI Number or State License Number.';
      return false;
    }

    if (isHcp === 'HCP_NOT_LICENSED' && !license) {
      this.error = 'Must enter State License Number.';
      return false;
    }

    return true;
  }

  private moveToStep2() {
    // Defer state change to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.step = 2;
      this.registrationForm.patchValue({ step: 2 });
      this.cdr.detectChanges();
    }, 0);
  }

  checkCallPlan(formData: RegistrationFormData) {
    const catsNum = formData.cats_num;
    const license = formData.license;
    const licenseState = formData.license_state;
    
    if (catsNum || (license && licenseState)) {
      // Defer state change to avoid ExpressionChangedAfterItHasBeenCheckedError
      setTimeout(() => {
        this.checkingCallPlan = true;
        this.error = null;
        this.cdr.detectChanges();
      }, 0);
      
      const callPlanObservable = environment.features.callPlan
        ? this.callPlanService.verifyCallPlan({
            cats_num: catsNum,
            first_name: formData.first_name,
            last_name: formData.last_name,
            license: license,
            license_state: licenseState,
            is_hcp: formData.is_hcp
          })
        : this.attendeeService.getCallPlan({
            cats_num: catsNum,
            first_name: formData.first_name,
            last_name: formData.last_name,
            license: license,
            license_state: licenseState,
            is_hcp: formData.is_hcp
          });
      
      callPlanObservable.pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (callPlan) => {
          this.checkingCallPlan = false;
          if (callPlan) {
            this.callPlanData = callPlan;
            this.confirmDialogVisible = true;
          } else {
            this.moveToStep2();
          }
        },
        error: (err) => {
          this.checkingCallPlan = false;
          console.error('Error checking call plan:', err);
          this.moveToStep2();
        }
      });
    } else {
      this.moveToStep2();
    }
  }

  confirmCallPlan() {
    if (!this.callPlanData) {
      this.confirmDialogVisible = false;
      this.moveToStep2();
      return;
    }

    const patchData: Record<string, unknown> = {
      'compass_id': this.callPlanData.compass_id || '',
      'call_plan_id': this.callPlanData.id ? Number(this.callPlanData.id) : null,
      'auto_populate': '1',
      'step': 2,
      'license_state': this.getFormValueOrCallPlan('license_state', this.callPlanData.license_state),
      'license': this.getFormValueOrCallPlan('license', this.callPlanData.license),
      'degrees': this.getFormValueOrCallPlan('degrees', this.callPlanData.degrees),
      'subspecialty': this.getFormValueOrCallPlan('subspecialty', this.callPlanData.specialty),
      'contact_type': this.getFormValueOrCallPlan('contact_type', this.callPlanData.contact_type),
      'office': {
        'affiliation': this.getFormValueOrCallPlan('office.affiliation', this.callPlanData.affiliation),
        'address1': this.getFormValueOrCallPlan('office.address1', this.callPlanData.address1),
        'address2': this.getFormValueOrCallPlan('office.address2', this.callPlanData.address2),
        'city': this.getFormValueOrCallPlan('office.city', this.callPlanData.city),
        'state': this.getFormValueOrCallPlan('office.state', this.callPlanData.state),
        'zip': this.getFormValueOrCallPlan('office.zip', this.callPlanData.zip),
        'email': this.registrationForm.get('office.email')?.value || ''
      },
      'confirm_email': this.registrationForm.get('office.email')?.value || ''
    };
    
    this.registrationForm.patchValue(patchData);
    this.clearValidatorsForCallPlanFields();
    
    this.confirmDialogVisible = false;
    this.isCallPlanVerified = true;
    this.submitRegistration();
  }

  private getFormValueOrCallPlan(fieldPath: string, callPlanValue?: string): string {
    return callPlanValue || this.registrationForm.get(fieldPath)?.value || '';
  }

  private clearValidatorsForCallPlanFields() {
    const fieldsToMakeOptional = [
      'degrees', 'subspecialty', 'contact_type', 'confirm_email'
    ];
    
    fieldsToMakeOptional.forEach(field => {
      const control = this.registrationForm.get(field);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
    
    // Clear validators for office fields
    const officeFields = ['affiliation', 'address1', 'city', 'state', 'zip', 'email'];
    officeFields.forEach(field => {
      const control = this.registrationForm.get(`office.${field}`);
      if (control) {
        control.clearValidators();
        control.updateValueAndValidity();
      }
    });
  }

  cancelCallPlan() {
    this.registrationForm.patchValue({ auto_populate: '0' });
    if (this.callPlanData) {
      this.registrationForm.patchValue({
        'cats_num': '',
        'license_state': '',
        'license': ''
      });
    }
    this.confirmDialogVisible = false;
    this.moveToStep2();
  }

  onStep2Submit(event?: SubmitEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    console.log('onStep2Submit called, form invalid:', this.registrationForm.invalid);
    
    // When using dynamic questions, validate required dynamic question fields
    if (this.useDynamicQuestions) {
      const invalidDynamicFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            const isEmpty = value === null || value === '' || value === undefined || 
                           (Array.isArray(value) && value.length === 0);
            if (control.invalid || isEmpty) {
              invalidDynamicFields.push(controlName);
              control.markAsTouched();
              console.log('Invalid field in step 2:', controlName, 'value:', value);
            }
          }
        }
      });
      
      if (invalidDynamicFields.length > 0) {
        console.log('Step 2 validation failed, invalid fields:', invalidDynamicFields);
        this.markFieldsAsMissing(invalidDynamicFields);
        this.error = 'Please complete all required fields.';
        this.cdr.detectChanges();
        return;
      }
    } else {
      if (this.registrationForm.invalid) {
        console.log('Form is invalid, marking missing fields');
        this.markMissingFields();
        this.cdr.detectChanges();
        return;
      }
    }

    console.log('Validation passed, submitting registration');
    this.submitRegistration();
  }

  submitRegistration() {
    console.log('submitRegistration called');
    
    if (!this.event) {
      console.error('No event available');
      this.error = 'Event information is missing. Please refresh the page and try again.';
      return;
    }

    console.log('Validating registration...');
    if (!this.validateRegistration()) {
      console.log('Registration validation failed');
      this.cdr.detectChanges();
      return;
    }

    console.log('Starting registration submission...');
    this.loading = true;
    this.error = null;
    this.cdr.detectChanges();

    // Build registration data based on whether we're using dynamic questions
    let registrationData: RegistrationFormData;
    
    if (this.useDynamicQuestions) {
      // Map dynamic question values to expected payload structure
      registrationData = this.buildRegistrationDataFromDynamicQuestions();
      console.log('Registration data from dynamic questions:', registrationData);
    } else {
      // Flatten the nested office object for API compatibility
      const formValue = this.registrationForm.value;
      console.log(formValue)
      registrationData = {
        ...formValue,
        step: 2,
        'office.affiliation': formValue.office?.affiliation || '',
        'office.address1': formValue.office?.address1 || '',
        'office.address2': formValue.office?.address2 || '',
        'office.city': formValue.office?.city || '',
        'office.state': formValue.office?.state || '',
        'office.zip': formValue.office?.zip || '',
        'office.phone': formValue.office?.phone || '',
        'office.email': formValue.office?.email || ''
      };
    }
    let token = this.event.token || this.route.snapshot.queryParams['token'];
    
    this.attendeeService.registerAttendee(this.event.id, registrationData, token).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (data: any) => {
        const attendeeId = data['attendee_id'];
        if (data['token']) {
          token = data['token'];
        }
        // If using dynamic questions, save registration answers
        if (this.useDynamicQuestions && this.registrationQuestions.length > 0 && attendeeId && this.event) {
          const answers: Record<number, any> = {};
          this.registrationQuestions.forEach(eq => {
            const question = eq.question;
            if (!question) return;
            const controlName = this.getQuestionControlName(question.id);
            const control = this.registrationForm.get(controlName);
            if (control && control.value !== null && control.value !== '' && 
                (Array.isArray(control.value) ? control.value.length > 0 : true)) {
              answers[question.id] = control.value;
            }
          });
          
          if (Object.keys(answers).length > 0) {
            this.attendeeService.saveRegistrationAnswers(this.event.id, attendeeId, answers, token).pipe(
              takeUntil(this.destroy$)
            ).subscribe({
              next: () => {
                this.navigateToThankYou(token);
              },
              error: (err) => {
                console.error('Error saving registration answers:', err);
                // Still navigate even if answers fail to save
                this.navigateToThankYou(token);
              }
            });
          } else {
            this.navigateToThankYou(token);
          }
        } else {
          this.navigateToThankYou(token);
        }
      },
      error: (err) => {
        console.error('Error submitting registration:', err);
        this.loading = false;
        this.error = err.error?.message || err.message || 'An error occurred while submitting your registration. Please try again.';
        this.cdr.detectChanges();
      }
    });
  }

  private validateRegistration(): boolean {
    // For dynamic questions, email validation might be in a dynamic question
    if (this.useDynamicQuestions) {
      // Check if email fields exist and validate them
      const emailControl = this.registrationForm.get('office.email');
      const confirmEmailControl = this.registrationForm.get('confirm_email');
      
      if (emailControl && confirmEmailControl) {
        const email = emailControl.value?.trim().toLowerCase() || '';
        const confirmEmail = confirmEmailControl.value?.trim().toLowerCase() || '';
        
        if (email && confirmEmail && email !== confirmEmail) {
          this.error = 'Email Address and Confirm Email Address do not match';
          this.markFieldsAsMissing(['office.email', 'confirm_email']);
          return false;
        }
      }
      
      // Validate all required dynamic questions are filled
      const invalidFields: string[] = [];
      this.registrationQuestions.forEach(eq => {
        const question = eq.question;
        if (!question) return;
        
        if (this.isQuestionRequired(eq) && this.isQuestionVisible(eq)) {
          const controlName = this.getQuestionControlName(question.id);
          const control = this.registrationForm.get(controlName);
          if (control) {
            const value = control.value;
            const isEmpty = value === null || value === '' || value === undefined || 
                           (Array.isArray(value) && value.length === 0);
            if (isEmpty) {
              invalidFields.push(controlName);
              control.markAsTouched();
            }
          }
        }
      });
      
      if (invalidFields.length > 0) {
        this.error = 'Please complete all required fields.';
        this.markFieldsAsMissing(invalidFields);
        return false;
      }

      // Validate confirm fields match their original questions
      for (const eq of this.registrationQuestions) {
        const question = eq.question;
        if (!question || !question.confirm || !this.isQuestionVisible(eq)) continue;
        const confirmError = this.getConfirmError(eq);
        if (confirmError) {
          this.error = confirmError;
          this.markFieldsAsMissing([`confirm_question_${question.id}`]);
          return false;
        }
        // Also validate confirm is filled when original is filled
        if (this.isConfirmRequired(eq)) {
          const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
          if (confirmControl && !confirmControl.value?.toString().trim()) {
            this.error = 'Please complete all required fields.';
            this.markFieldsAsMissing([`confirm_question_${question.id}`]);
            confirmControl.markAsTouched();
            return false;
          }
        }
      }
    } else {
      // Original validation for hardcoded form
      const email = this.registrationForm.get('office.email')?.value?.trim().toLowerCase() || '';
      const confirmEmail = this.registrationForm.get('confirm_email')?.value?.trim().toLowerCase() || '';
      
      if (email && confirmEmail && email !== confirmEmail) {
        this.error = 'Email Address and Confirm Email Address do not match';
        this.markFieldsAsMissing(['office.email']);
        return false;
      }
      
      if (!this.isCallPlanVerified) {
        if (this.registrationForm.invalid) {
          this.markMissingFields();
          return false;
        }
      } else {
        const firstName = this.registrationForm.get('first_name')?.value;
        const lastName = this.registrationForm.get('last_name')?.value;
        if (!firstName || !lastName) {
          this.error = 'First name and last name are required';
          return false;
        }
      }
    }
    
    return true;
  }

  markFieldsAsMissing(fields: string[]) {
    this.missingFields = fields;
    fields.forEach(field => {
      const control = this.registrationForm.get(field);
      if (control) {
        control.markAsTouched();
      }
    });
  }

  markMissingFields() {
    this.missingFields = [];
    Object.keys(this.registrationForm.controls).forEach(key => {
      const control = this.registrationForm.get(key);
      if (control && control.invalid && control.errors) {
        this.missingFields.push(key);
        control.markAsTouched();
      }
    });
  }

  isFieldMissing(fieldName: string): boolean {
    return this.missingFields.includes(fieldName);
  }

  formatEventTime(): string {
    return this.event?.start_time || '';
  }

  loadRegistrationQuestions(eventId: number | string, token?: string) {
    this.loadingQuestions = true;
    this.eventService.getRegistrationQuestions(eventId, token).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.loadingQuestions = false;
      })
    ).subscribe({
      next: (questions) => {
        if (questions && questions.length > 0) {
          this.registrationQuestions = questions;
          this.useDynamicQuestions = true;
          this.buildDynamicForm();
        } else {
          this.useDynamicQuestions = false;
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error loading registration questions:', err);
        this.useDynamicQuestions = false;
        this.cdr.detectChanges();
      }
    });
  }

  buildDynamicForm() {
    // Add dynamic question controls to existing form instead of rebuilding
    this.registrationQuestions.forEach(eq => {
      const question = eq.question;
      if (!question) return;
      
      const controlName = `question_${question.id}`;
      
      // Skip if control already exists
      if (this.registrationForm.get(controlName)) {
        return;
      }
      
      const validators = [];
      
      if (question.always_required) {
        validators.push(Validators.required);
      }
      
      let initialValue: any = '';
      if (question.type_id === 'CHECKBOX' || question.type_id === 'MULTIPLE_CHECK') {
        initialValue = [];
      } else if (question.type_id === 'TF' || question.type_id === 'YN') {
        initialValue = null;
      }
      
      this.registrationForm.addControl(controlName, this.fb.control(initialValue, validators));

      // If question has confirm flag, create a confirm control
      if (question.confirm) {
        const confirmControlName = `confirm_question_${question.id}`;
        if (!this.registrationForm.get(confirmControlName)) {
          this.registrationForm.addControl(confirmControlName, this.fb.control(''));
        }
      }

      // Subscribe to value changes for fields that might affect visibility of other fields
      // This ensures "other" fields (like other_specialties) update visibility when parent field changes
      const control = this.registrationForm.get(controlName);
      if (control && (question.field_id === 'specialties' || question.field_id === 'degrees')) {
        control.valueChanges.pipe(
          takeUntil(this.destroy$)
        ).subscribe(() => {
          // Trigger change detection to update visibility of dependent fields
          this.cdr.detectChanges();
        });
      }
    });
  }

  getQuestionControlName(questionId: number): string {
    return `question_${questionId}`;
  }

  getQuestionChoices(question: any): string[] {
    // Use component properties for degrees (fetched from company dropdown: /dropdowns/attendee_degree)
    if (question.field_id === 'degrees' && this.degreeOptions.length > 0) {
      return this.degreeOptions;
    }
    
    // Use component properties for specialties (fetched from company dropdown: /dropdowns/attendee_specialty)
    if (question.field_id === 'specialties' && this.specialtyOptions.length > 0) {
      return this.specialtyOptions;
    }
    
    // Use component properties for state fields (fetched from common dropdown: /dropdowns/country_state)
    // This covers both address.state and license.state fields
    if ((question.field_id === 'address.state' || question.field_id === 'license.state') && this.stateOptions.length > 0) {
      return this.stateOptions;
    }
    
    // Fallback to question choices for other fields (NOT for degrees, specialties, or states)
    // Do NOT make direct API calls to /dropdowns/:name endpoints
    if (!question.choices) return [];
    try {
      if (typeof question.choices === 'string') {
        if (question.choices.startsWith('[') || question.choices.startsWith('{')) {
          return JSON.parse(question.choices);
        }
        // Check if choices are separated by newlines
        if (question.choices.includes('\n')) {
          return question.choices.split('\n').map((c: string) => c.trim()).filter((c: string) => c !== '');
        }
        // Otherwise split by comma
        return question.choices.split(',').map((c: string) => c.trim());
      }
      return Array.isArray(question.choices) ? question.choices : [];
    } catch {
      // Try splitting by newline first, then comma
      if (typeof question.choices === 'string' && question.choices.includes('\n')) {
        return question.choices.split('\n').map((c: string) => c.trim()).filter((c: string) => c !== '');
      }
      return question.choices.split(',').map((c: string) => c.trim());
    }
  }

  isQuestionVisible(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question || !question.visibility_condition_applied) return true;
    
    let subjectControl;
    
    // If visibility_condition_subject_id is provided, find the question by matching question_id
    if (question.visibility_condition_subject_id) {
      // Find the question where question.id matches visibility_condition_subject_id
      const subjectQuestion = this.registrationQuestions.find(eq => 
        eq.question?.id === question.visibility_condition_subject_id
      );
      if (subjectQuestion?.question) {
        subjectControl = this.registrationForm.get(this.getQuestionControlName(subjectQuestion.question.id));
      }
    } else if (question.field_id) {
      // For "other" fields, find the parent question by field_id pattern
      // e.g., "other_specialties" -> find question with field_id "specialties"
      // e.g., "other_degree" -> find question with field_id "degrees"
      if (question.field_id.startsWith('other_')) {
        const parentFieldId = question.field_id.replace('other_', '');
        // Find the parent question
        const parentQuestion = this.registrationQuestions.find(eq => 
          eq.question?.field_id === parentFieldId || 
          eq.question?.field_id === parentFieldId.replace('s$', '') // Handle plural/singular
        );
        if (parentQuestion?.question) {
          subjectControl = this.registrationForm.get(this.getQuestionControlName(parentQuestion.question.id));
        }
      }
    }
    
    if (!subjectControl) return true;
    
    const subjectValue = subjectControl.value;
    const operator = question.visibility_condition_operator;
    const conditionValue = question.visibility_condition_value;
    const visibilityCommand = question.visibility_command || 'Display';
    
    // Check if the condition matches
    let conditionMatches = false;
    
    // Handle array values (for MULTIPLE_CHECK fields)
    if (Array.isArray(subjectValue)) {
      if (operator === '===' || operator === '=' || operator === 'Equals') {
        conditionMatches = subjectValue.includes(conditionValue);
      } else if (operator === '!==' || operator === 'Not Equals') {
        conditionMatches = !subjectValue.includes(conditionValue);
      }
    } else {
      // Handle single values
      if (operator === '===' || operator === '=' || operator === 'Equals') {
        conditionMatches = String(subjectValue) === String(conditionValue);
      } else if (operator === '!==' || operator === 'Not Equals') {
        conditionMatches = String(subjectValue) !== String(conditionValue);
      }
    }
    
    // Apply visibility command: "Display" means show when condition matches, "Hide" means hide when condition matches
    if (visibilityCommand === 'Hide') {
      return !conditionMatches; // Hide when condition matches, show when it doesn't
    } else {
      return conditionMatches; // Display when condition matches, hide when it doesn't
    }
  }

  isQuestionRequired(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question) return false;
    
    if (question.always_required) return true;
    
    if (question.required_if_question_id) {
      const requiredIfControl = this.registrationForm.get(this.getQuestionControlName(question.required_if_question_id));
      if (requiredIfControl) {
        const value = requiredIfControl.value;
        if (question.required_if_value) {
          return String(value) === String(question.required_if_value);
        }
        return !!value;
      }
    }
    
    return false;
  }

  isConfirmRequired(eq: EvaluationQuestion): boolean {
    const question = eq.question;
    if (!question || !question.confirm) return false;
    if (this.isQuestionRequired(eq)) return true;
    const control = this.registrationForm.get(this.getQuestionControlName(question.id));
    return !!control && !!control.value;
  }

  getConfirmError(eq: EvaluationQuestion): string | null {
    const question = eq.question;
    if (!question || !question.confirm) return null;
    const originalControl = this.registrationForm.get(this.getQuestionControlName(question.id));
    const confirmControl = this.registrationForm.get(`confirm_question_${question.id}`);
    if (!originalControl || !confirmControl) return null;
    const originalValue = (originalControl.value || '').toString().trim();
    const confirmValue = (confirmControl.value || '').toString().trim();
    if (originalValue && confirmValue && originalValue.toLowerCase() !== confirmValue.toLowerCase()) {
      return 'The responses do NOT match';
    }
    return null;
  }

  isChoiceSelected(questionId: number, choice: string): boolean {
    const control = this.registrationForm.get(this.getQuestionControlName(questionId));
    if (control && Array.isArray(control.value)) {
      return control.value.includes(choice);
    }
    return false;
  }

  onCheckboxChange(questionId: number, choice: string, event: any) {
    const control = this.registrationForm.get(this.getQuestionControlName(questionId));
    if (control) {
      const currentValue = control.value || [];
      const target = (event.target as HTMLInputElement);
      if (target && target.checked) {
        if (!currentValue.includes(choice)) {
          control.setValue([...currentValue, choice]);
        }
      } else if (target) {
        control.setValue(currentValue.filter((v: string) => v !== choice));
      }
    }
  }

  private buildRegistrationDataFromDynamicQuestions(): RegistrationFormData {
    const registrationData: RegistrationFormData = {
      step: 2,
      first_name: '',
      middle_name: '',
      last_name: '',
      degrees: '',
      degrees_other: '',
      subspecialty: '',
      subspecialty_other: '',
      'office.affiliation': '',
      'office.address1': '',
      'office.address2': '',
      'office.city': '',
      'office.state': '',
      'office.zip': '',
      'office.phone': '',
      'office.email': '',
      confirm_email: '',
      cats_num: '',
      license: '',
      license_state: ''
    };

    console.log('Building registration data from dynamic questions...');
    
    // Map dynamic question values to registration data based on field_id
    this.registrationQuestions.forEach(eq => {
      const question = eq.question;
      if (!question || !question.field_id) {
        console.log('Skipping question without field_id:', question);
        return;
      }

      const controlName = this.getQuestionControlName(question.id);
      const control = this.registrationForm.get(controlName);
      if (!control) {
        console.log('Control not found for question:', controlName, question);
        return;
      }

      const value = control.value;
      const fieldId = question.field_id;
      console.log(`Mapping field_id: ${fieldId}, value:`, value, 'type:', question.type_id);

      // Map based on field_id
      switch (fieldId) {
        case 'first_name':
          registrationData.first_name = value || '';
          break;
        case 'middle_name':
          registrationData.middle_name = value || '';
          break;
        case 'last_name':
          registrationData.last_name = value || '';
          break;
        case 'degrees':
          // MULTIPLE_CHECK returns an array of actual values
          // The registerAttendee API expects a string (it will convert to array)
          // But the saveRegistrationAnswers will save the array directly
          if (Array.isArray(value) && value.length > 0) {
            registrationData.degrees = value.join(','); // Join array values for registerAttendee
          } else if (value && !Array.isArray(value)) {
            registrationData.degrees = value;
          }
          break;
        case 'other_degree':
          registrationData.degrees_other = value || '';
          break;
        case 'specialties':
          // MULTIPLE_CHECK returns an array of actual values
          // The registerAttendee API expects a string (it will convert to array)
          // But the saveRegistrationAnswers will save the array directly
          if (Array.isArray(value) && value.length > 0) {
            registrationData.subspecialty = value.join(','); // Join array values for registerAttendee
          } else if (value && !Array.isArray(value)) {
            registrationData.subspecialty = value;
          }
          break;
        case 'other_specialties':
          registrationData.subspecialty_other = value || '';
          break;
        case 'npi_no':
          registrationData.cats_num = value || '';
          break;
        case 'license.license':
          registrationData.license = value || '';
          break;
        case 'license.state':
          registrationData.license_state = value || '';
          break;
        case 'address.affiliation':
          registrationData['office.affiliation'] = value || '';
          break;
        case 'address.address1':
          registrationData['office.address1'] = value || '';
          break;
        case 'address.address2':
          registrationData['office.address2'] = value || '';
          break;
        case 'address.city':
          registrationData['office.city'] = value || '';
          break;
        case 'address.state':
          registrationData['office.state'] = value || '';
          break;
        case 'address.zip':
          registrationData['office.zip'] = value || '';
          break;
        case 'address.phone':
          registrationData['office.phone'] = value || '';
          break;
        case 'address.email':
        case 'email':
          registrationData['office.email'] = value || '';
          break;
        case 'contact_type':
          registrationData['contact_type'] = value || '';
          break;

      }
    });

    // Get confirm_email from dynamic confirm field or static form control
    const emailQuestion = this.registrationQuestions.find(eq => eq.question?.field_id === 'address.email' && eq.question?.confirm);
    if (emailQuestion?.question) {
      const confirmControl = this.registrationForm.get(`confirm_question_${emailQuestion.question.id}`);
      if (confirmControl) {
        registrationData.confirm_email = confirmControl.value || '';
      }
    } else {
      const confirmEmailControl = this.registrationForm.get('confirm_email');
      if (confirmEmailControl) {
        registrationData.confirm_email = confirmEmailControl.value || '';
      }
    }

    console.log('Final registration data:', registrationData);
    return registrationData;
  }

  private navigateToThankYou(token?: string) {
    this.isNavigating = true;
    this.loading = false;
    
    const navToken = token || this.event?.token || this.route.snapshot.queryParams['token'];
    if (navToken) {
      this.router.navigate([environment.routes.thankYou], {
        queryParams: { 
          token: navToken
        },
        replaceUrl: true
      }).catch(() => {
        this.isNavigating = false;
        this.error = 'Registration successful, but there was an error navigating to the confirmation page.';
      });
    } else {
      this.isNavigating = false;
      this.error = 'Registration successful, but no token available for navigation.';
    }
  }
}
